import { useLayoutStore } from '@/stores/layoutStore';
import ChartData from '@/helper-classes/inspector/ChartData';
import Rectangle from '@/helper-classes/inspector/Rectangle';
import { formatNumber } from '@/utils/formatNumber';
import { getWrappedWordsForCanvas } from '@/utils/getWrappedWordsForCanvas';
import { throttle } from '@/utils/throttle';

const VERTICAL = 'vertical';
const HORIZONTAL = 'horizontal';

const BLUE_COLOR = '#00F';
const WHITE_COLOR = '#FFF';
const GRAY_COLOR = '#5a6672';
const LIGHT_GRAY_COLOR = '#DDD';
const BLACK_COLOR = '#16191d';

const INFO_BOX_FONT_HEIGHT = 10;
const LABEL_FONT_HEIGHT = 8;

function createFontString(fontSize) {
  return `${fontSize}pt 'Roboto Condensed','Lato','Helvetica Neue',Helvetica,Arial,sans-serif`;
}

export default class Inspector {
  chartData;
  onScreenCanvasElement;
  offScreenCanvasElement;
  onScreenCanvasContext;
  offScreenCanvasContext;
  mouseX = null;
  mouseY = null;
  mouseDownX = null;
  mouseDownY = null;
  direction = null;
  shiftDown = false;
  ctrlDown = false;
  isMobile = false;
  isDestroyed = false;

  constructor({ chartDataString, onScreenCanvasElement, offScreenCanvasElement }) {
    this.chartData = new ChartData(chartDataString);
    this.onScreenCanvasElement = onScreenCanvasElement;
    this.offScreenCanvasElement = offScreenCanvasElement;
    this.onScreenCanvasContext = this.onScreenCanvasElement.getContext('2d');
    this.offScreenCanvasContext = this.offScreenCanvasElement.getContext('2d');
    this.paint = throttle(this._paint.bind(this), 33, true);

    // Position and size canvas
    this.onScreenCanvasElement.width = this.chartData.width;
    this.onScreenCanvasElement.height = this.chartData.height;
    this.offScreenCanvasElement.width = this.onScreenCanvasElement.width;
    this.offScreenCanvasElement.height = this.onScreenCanvasElement.height;

    if (
      navigator.userAgent.match(/iPad/i) !== null ||
      navigator.userAgent.match(/iPhone/i) !== null ||
      navigator.userAgent.match(/Android/i) !== null
    ) {
      this.isMobile = true; // TODO: Improve this mobile device detection logic
    }
  }

  setMouseXAndMouseY(e, shouldSetMouseDownXAndMouseDownY) {
    const xPropertyName = shouldSetMouseDownXAndMouseDownY ? 'mouseDownX' : 'mouseX';
    const yPropertyName = shouldSetMouseDownXAndMouseDownY ? 'mouseDownY' : 'mouseY';
    const onScreenCanvasBoundingClientRect = this.onScreenCanvasElement.getBoundingClientRect();
    this[xPropertyName] = Math.round(e.clientX - onScreenCanvasBoundingClientRect.left);
    this[yPropertyName] = Math.round(e.clientY - onScreenCanvasBoundingClientRect.top);
    const r = this.chartData.panelRectangle.clone();
    const currentIndex = this.chartData.getIndexForX(this[xPropertyName]);

    if (
      this[xPropertyName] < r.x ||
      this[xPropertyName] > r.x + r.w ||
      this[yPropertyName] < r.y ||
      this[yPropertyName] > r.y + r.height ||
      currentIndex === -1
    ) {
      this[xPropertyName] = null;
      this[yPropertyName] = null;
    }
  }

  isMouseOverInspectorBounds() {
    return (!!this.mouseX || this.mouseX === 0) && (!!this.mouseY || this.mouseY === 0);
  }

  isMouseDownAndOverInspectorBounds() {
    return (!!this.mouseDownX || this.mouseDownX === 0) && (!!this.mouseDownY || this.mouseDownY === 0);
  }

  setDirection() {
    if (
      !this.isMouseOverInspectorBounds() ||
      this.direction !== null ||
      !this.isMouseDownAndOverInspectorBounds()
    ) {
      return;
    }

    const dX = Math.abs(this.mouseX - this.mouseDownX);
    const dY = Math.abs(this.mouseY - this.mouseDownY);

    if (dX <= 5 && dY <= 5) {
      return;
    }

    this.direction = dX > dY ? HORIZONTAL : VERTICAL;
  }

  drawLine(canvasContext, x1, y1, x2, y2, stroke) {
    x1 = Math.floor(x1);
    y1 = Math.floor(y1);
    x2 = Math.floor(x2);
    y2 = Math.floor(y2);

    // Coordinate system lies between pixels.
    // If we have an odd line width then lighter lines will end up
    // being drawn to either side of the coordinate resulting
    // in fuzzy, stupid-looking lines.
    // Read 'A lineWidth example' here: https://developer.mozilla.org/en-US/docs/HTML/Canvas/Tutorial/Applying_styles_and_colors?redirectlocale=en-US&redirectslug=Canvas_tutorial%2FApplying_styles_and_colors
    if (canvasContext.lineWidth % 2 !== 0) {
      // Vertical
      if (x1 === x2) {
        x1 += .5;
        x2 += .5;
      }

      // Horizontal
      if (y1 === y2) {
        y1 += .5;
        y2 += .5;
      }
    }

    if (stroke || stroke === undefined) {
      canvasContext.beginPath();
    }

    canvasContext.moveTo(x1, y1);
    canvasContext.lineTo(x2, y2);

    // Calling stroke() is expensive.
    // Allow batching of moveTo/lineTo.
    if (stroke || stroke === undefined) {
      canvasContext.stroke();
    }
  }

  drawDashedLine(canvasContext, x1, y1, x2, y2, dashLen) {
    if (dashLen === undefined) {
      dashLen = 2;
    }

    canvasContext.moveTo(x1, y1);

    const dX = x2 - x1;
    const dY = y2 - y1;
    const dashes = Math.floor(Math.sqrt(dX * dX + dY * dY) / dashLen);
    let dashX = dX / dashes;
    let dashY = dY / dashes;

    if (dashX > 0) {
      dashX = Math.floor(dashX);
    } else {
      dashX = Math.ceil(dashX);
    }

    if (dashY > 0) {
      dashY = Math.floor(dashY);
    } else {
      dashY = Math.ceil(dashY);
    }

    let q = 0;

    while (q++ < dashes) {
      x1 += dashX;
      y1 += dashY;
      canvasContext[q % 2 === 0 ? 'moveTo' : 'lineTo'](x1, y1);
    }

    canvasContext[q % 2 === 0 ? 'moveTo' : 'lineTo'](x2, y2);
  }

  fillPolygon(canvasContext, points) {
    canvasContext.beginPath();
    canvasContext.moveTo(Math.floor(points[0]), Math.floor(points[1]));

    for (let i = 2; i < points.length - 1; i += 2) {
      canvasContext.lineTo(Math.floor(points[i]), Math.floor(points[i + 1]));
    }

    canvasContext.fill();
  }

  strokePolygon(canvasContext, points) {
    canvasContext.beginPath();
    canvasContext.moveTo(Math.floor(points[0]), Math.floor(points[1]));

    for (let i = 2; i < points.length - 1; i += 2) {
      canvasContext.lineTo(Math.floor(points[i]), Math.floor(points[i + 1]));
    }

    canvasContext.stroke();
  }

  fillRect(canvasContext, x, y, width, height) {
    x = Math.floor(x);
    y = Math.floor(y);
    width = Math.floor(width);
    height = Math.floor(height);

    canvasContext.fillRect(x, y, width, height);
  }

  clearCanvas(canvas) {
    if (!canvas) {
      return;
    }

    const canvasContext = canvas.getContext('2d');

    // Store the current transformation matrix
    canvasContext.save();

    // Use the identity matrix while clearing the canvas
    canvasContext.setTransform(1, 0, 0, 1, 0, 0);
    canvasContext.clearRect(0, 0, canvas.width, canvas.height);

    // Restore the transform
    canvasContext.restore();
  }

  _paint() {
    const layoutStore = useLayoutStore();

    if (this.mouseX === null || this.mouseY === null || this.isDestroyed) {
      this.clearCanvas(this.onScreenCanvasElement);

      layoutStore.$patch({ isInspectorInUse: false });
      return;
    }

    this.clearCanvas(this.offScreenCanvasElement);

    let panel = this.chartData.getPanelForY(this.mouseY);

    if (panel === null || panel.isFullQuotePanel) {
      this.clearCanvas(this.onScreenCanvasElement);

      layoutStore.$patch({ isInspectorInUse: false });
      return;
    }

    layoutStore.$patch({ isInspectorInUse: true });

    let topY = this.chartData.panelRectangle.y;
    const height = this.chartData.panelRectangle.height;
    let index = this.chartData.getIndexForX(this.mouseX, true);
    const barWidth = this.chartData.getBarWidthForIndex(index);
    let startX = this.chartData.getXForIndex(index) - Math.floor(barWidth / 2);
    let left = this.chartData.panelRectangle.x;
    const width = this.chartData.panelRectangle.width;
    let drawY = this.mouseY;
    let numPeriods;
    let labelMargin;
    let endIndex;

    if (drawY < topY) {
      drawY = topY;
    }

    if (drawY > topY + height) {
      drawY = topY + height;
    }

    this.offScreenCanvasContext.font = createFontString(LABEL_FONT_HEIGHT);

    // Crosshairs
    if (this.shiftDown) {
      this.offScreenCanvasContext.strokeStyle = BLUE_COLOR;

      if ((this.mouseDownX === null || this.mouseDownY === null) || this.direction !== HORIZONTAL) {
        this.drawLine(this.offScreenCanvasContext, this.mouseX, topY, this.mouseX, topY + height);
      }

      if (this.mouseDownX === null || this.mouseDownY === null) {
        this.drawLine(this.offScreenCanvasContext, left, drawY, left + width, drawY);
      }
    } else {
      this.offScreenCanvasContext.globalAlpha = 0.4;
      this.offScreenCanvasContext.fillStyle = GRAY_COLOR;

      if ((this.mouseDownX === null || this.mouseDownY === null) || this.direction !== HORIZONTAL) {
        this.fillRect(this.offScreenCanvasContext, startX, topY, barWidth, height);
      }

      if (this.mouseDownX === null || this.mouseDownY === null) {
        this.fillRect(this.offScreenCanvasContext, left, drawY, width, 3);
      }
    }

    this.offScreenCanvasContext.globalAlpha = 1;

    // Ruler
    if (this.mouseDownX !== null && this.mouseDownY !== null && this.direction !== null) {
      if (this.direction === HORIZONTAL) {
        // Start line
        const startIndex = this.chartData.getIndexForX(this.mouseDownX, true);
        startX = this.chartData.getXForIndex(startIndex);
        this.offScreenCanvasContext.strokeStyle = BLUE_COLOR;
        this.drawLine(this.offScreenCanvasContext, startX, topY, startX, topY + height);

        // End line
        endIndex = this.chartData.getIndexForX(this.mouseX, true);
        const endX = this.chartData.getXForIndex(endIndex);
        this.drawLine(this.offScreenCanvasContext, endX, topY, endX, topY + height);

        // Horizontal dashed line
        const midY = topY + height / 2;
        this.drawDashedLine(this.offScreenCanvasContext, startX, midY, endX, midY, 4);
        this.offScreenCanvasContext.stroke();

        numPeriods = Math.abs(endIndex - startIndex);
      } else {
        // Start line
        startX = this.chartData.panelRectangle.x;
        const endX = startX + this.chartData.panelRectangle.width;
        this.offScreenCanvasContext.strokeStyle = BLUE_COLOR;
        this.drawLine(this.offScreenCanvasContext, startX, this.mouseDownY, endX, this.mouseDownY);

        // End line
        this.drawLine(this.offScreenCanvasContext, startX, this.mouseY, endX, this.mouseY);

        // Vertical dashed line
        const midX = (startX + endX) / 2;
        this.drawDashedLine(this.offScreenCanvasContext, midX, this.mouseDownY, midX, this.mouseY, 4);
        this.offScreenCanvasContext.stroke();
      }
    }

    // y-axis value label on right
    let labelPadding = 2;
    let lineSpacing = 4;
    const yValueLabel = formatNumber(panel.getValueForY(this.mouseY), {
      desiredNumberOfDecimals: panel.getNumberOfDecimalPlaces()
    });
    let labelX = left + width + labelPadding;
    let labelY = this.mouseY + LABEL_FONT_HEIGHT;
    let lines = getWrappedWordsForCanvas(this.offScreenCanvasContext, yValueLabel, labelX, labelY, (this.onScreenCanvasElement.width - labelX) - labelPadding, LABEL_FONT_HEIGHT + lineSpacing);
    let labelTextWidth = 0;
    let labelTextHeight = LABEL_FONT_HEIGHT;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const w = this.offScreenCanvasContext.measureText(line.line).width;

      if (w > labelTextWidth) {
        labelTextWidth = w;
      }

      if (i > 0) {
        labelTextHeight += LABEL_FONT_HEIGHT + lineSpacing;
      }
    }

    const labelYOffset = labelTextHeight / 2;
    labelX -= labelPadding;
    labelY -= labelYOffset + LABEL_FONT_HEIGHT + labelPadding;

    // Background of label
    let points = [];

    // Top left
    points.push(labelX);
    points.push(labelY);

    // Top right
    points.push(labelX + labelTextWidth + labelPadding * 2);
    points.push(labelY);

    // Bottom right
    points.push(labelX + labelTextWidth + labelPadding * 2);
    points.push(labelY + labelTextHeight + labelPadding * 2);

    // Bottom left
    points.push(labelX);
    points.push(labelY + labelTextHeight + labelPadding * 2);

    // Arrow point
    points.push(labelX - labelPadding);
    points.push(labelY + (labelTextHeight + labelPadding * 2) / 2);

    // Top left
    points.push(labelX);
    points.push(labelY);

    this.offScreenCanvasContext.fillStyle = LIGHT_GRAY_COLOR;
    this.fillPolygon(this.offScreenCanvasContext, points);
    this.offScreenCanvasContext.strokeStyle = BLUE_COLOR;
    this.strokePolygon(this.offScreenCanvasContext, points);

    this.offScreenCanvasContext.fillStyle = BLACK_COLOR;
    this.offScreenCanvasContext.font = createFontString(LABEL_FONT_HEIGHT);

    for (const line of lines) {
      this.offScreenCanvasContext.fillText(line.line, line.x, line.y - labelYOffset);
    }

    // x-axis labels
    index = this.chartData.getIndexForX(this.mouseX, true);
    const curDateInfo = this.chartData.getDateInfoForIndex(index);
    let labelText = '';

    if (this.chartData.isIntraday) {
      labelText = curDateInfo.startDateID.getFullIntradayDateString();
    } else {
      labelText = curDateInfo.startDateID.getAbbreviatedDateString() + ' ' + curDateInfo.startDateID.year;
    }

    labelTextWidth = this.offScreenCanvasContext.measureText(labelText).width;
    labelX = this.chartData.getXForIndex(index) - labelTextWidth / 2;

    if (labelX < 0) {
      labelX = 0;
    }

    if (labelX + labelTextWidth + labelPadding > this.onScreenCanvasElement.width) {
      labelX = this.onScreenCanvasElement.width - (labelTextWidth + labelPadding * 2);
    }

    // Top x-axis label
    if (this.chartData.panelRectangle.y > 35) {
      labelY = this.chartData.panelRectangle.y - (labelPadding + 1);
      points = [];

      // Bottom left
      points.push(labelX - labelPadding);
      points.push(labelY + labelPadding);

      // Bottom right
      points.push(labelX + labelPadding + labelTextWidth);
      points.push(labelY + labelPadding);

      // Top right
      points.push(labelX + labelPadding + labelTextWidth);
      points.push(labelY - (LABEL_FONT_HEIGHT + labelPadding));

      // Top left
      points.push(labelX - labelPadding);
      points.push(labelY - (LABEL_FONT_HEIGHT + labelPadding));

      // Bottom left
      points.push(labelX - labelPadding);
      points.push(labelY + labelPadding);

      this.offScreenCanvasContext.fillStyle = LIGHT_GRAY_COLOR;
      this.fillPolygon(this.offScreenCanvasContext, points);
      this.offScreenCanvasContext.strokeStyle = BLUE_COLOR;
      this.strokePolygon(this.offScreenCanvasContext, points);

      this.offScreenCanvasContext.fillStyle = BLACK_COLOR;
      this.offScreenCanvasContext.fillText(labelText, labelX, labelY);
    }

    // Bottom x-axis label
    labelY = this.chartData.panelRectangle.y + this.chartData.panelRectangle.height + LABEL_FONT_HEIGHT + labelPadding + 1;
    points = [];

    // Bottom left
    points.push(labelX - labelPadding);
    points.push(labelY + labelPadding);

    // Bottom right
    points.push(labelX + labelPadding + labelTextWidth);
    points.push(labelY + labelPadding);

    // Top right
    points.push(labelX + labelPadding + labelTextWidth);
    points.push(labelY - (LABEL_FONT_HEIGHT + labelPadding));

    // Top left
    points.push(labelX - labelPadding);
    points.push(labelY - (LABEL_FONT_HEIGHT + labelPadding));

    // Bottom left
    points.push(labelX - labelPadding);
    points.push(labelY + labelPadding);

    this.offScreenCanvasContext.fillStyle = LIGHT_GRAY_COLOR;
    this.fillPolygon(this.offScreenCanvasContext, points);
    this.offScreenCanvasContext.strokeStyle = BLUE_COLOR;
    this.strokePolygon(this.offScreenCanvasContext, points);

    this.offScreenCanvasContext.fillStyle = BLACK_COLOR;
    this.offScreenCanvasContext.fillText(labelText, labelX, labelY);

    for (const hAxisPanel of this.chartData.hAxisPanels) {
      labelY = hAxisPanel.bounds.y + LABEL_FONT_HEIGHT + labelPadding + 1;
      points = [];

      // Bottom left
      points.push(labelX - labelPadding);
      points.push(labelY + labelPadding);

      // Bottom right
      points.push(labelX + labelPadding + labelTextWidth);
      points.push(labelY + labelPadding);

      // Top right
      points.push(labelX + labelPadding + labelTextWidth);
      points.push(labelY - (LABEL_FONT_HEIGHT + labelPadding));

      // Top left
      points.push(labelX - labelPadding);
      points.push(labelY - (LABEL_FONT_HEIGHT + labelPadding));

      // Bottom left
      points.push(labelX - labelPadding);
      points.push(labelY + labelPadding);

      this.offScreenCanvasContext.fillStyle = LIGHT_GRAY_COLOR;
      this.fillPolygon(this.offScreenCanvasContext, points);
      this.offScreenCanvasContext.strokeStyle = BLUE_COLOR;
      this.strokePolygon(this.offScreenCanvasContext, points);

      this.offScreenCanvasContext.fillStyle = BLACK_COLOR;
      this.offScreenCanvasContext.fillText(labelText, labelX, labelY);
    }

    // Info box
    this.offScreenCanvasContext.font = createFontString(INFO_BOX_FONT_HEIGHT);
    if ((this.mouseDownX === null || this.mouseDownY === null) && !this.shiftDown && !this.ctrlDown) {
      labelPadding = 4;
      labelMargin = this.isMobile ? 50 : 20;
      lineSpacing = 6;
      const dp = this.chartData.getPanelForY(this.mouseY);
      let labelHeight = 0;
      let labelWidth = 0;
      const splitLines = []

      panel = this.chartData.getPanelForY(this.mouseY);
      const yValue = panel.getValueForY(this.mouseY);

      if (dp.isPricePanel) {
        const [open, high, low, close] = curDateInfo.prices;
        const formatInspectValue = (value, overrideOptions = {}) => isNaN(value) ? '' : formatNumber(value, { desiredNumberOfDecimals: dp.getNumberOfDecimalPlaces(), shouldInsertCommas: true, ...overrideOptions })

        splitLines.push([labelText, '']);
        splitLines.push(['O: ', formatInspectValue(open)]);
        splitLines.push(['H: ', formatInspectValue(high)]);
        splitLines.push(['L: ', formatInspectValue(low)]);
        splitLines.push(['C: ', formatInspectValue(close)]);

        if (curDateInfo.percentChange !== null) {
          splitLines.push(['%: ', formatInspectValue(curDateInfo.percentChange, { includeSign: true }) + '%']);
        }

        splitLines.push(['V: ', formatInspectValue(curDateInfo.volume, { desiredNumberOfDecimals: 0 })]);
        splitLines.push(['Y: ', formatInspectValue(yValue)])

        const NUMBER_OF_INFO_LINES = splitLines.length - 1;
        labelHeight = (NUMBER_OF_INFO_LINES * INFO_BOX_FONT_HEIGHT) + ((NUMBER_OF_INFO_LINES - 1) * lineSpacing) + (2 * labelPadding);
      } else {
        labelHeight = INFO_BOX_FONT_HEIGHT + (2 * labelPadding);
        splitLines.push([labelText, '']);
        splitLines.push(['Y: ', formatNumber(yValue, { desiredNumberOfDecimals: dp.getNumberOfDecimalPlaces(), shouldInsertCommas: true })]);
      }

      const lineWidths = splitLines.map(line => {
        const lineString = line.join('');
        return this.offScreenCanvasContext.measureText(lineString).width;
      });
      const longestLineWidth = Math.max(...lineWidths);

      labelWidth = longestLineWidth + (labelPadding * 2);
      labelX = this.mouseX - (labelWidth + labelMargin);
      labelY = this.mouseY;
      points = [];

      // Bounds checks
      if (labelX + labelWidth >= this.chartData.panelRectangle.x + this.chartData.panelRectangle.width) {
        labelX = this.mouseX - (labelWidth + labelMargin);
      }

      if (labelX < this.chartData.panelRectangle.x) {
        labelX = this.mouseX + labelMargin;
      }

      if (labelY + labelHeight >= this.chartData.panelRectangle.y + this.chartData.panelRectangle.height) {
        labelY = this.mouseY - (labelHeight + labelMargin);
      }

      if (labelY < this.chartData.panelRectangle.y) {
        labelY = this.chartData.panelRectangle.y;
      }

      // Try to keep info box off of price bars
      if (panel.isPricePanel) {
        let startIndex = this.chartData.getIndexForX(labelX, true) - 1;
        endIndex = this.chartData.getIndexForX(labelX + labelWidth, true) + 1;

        if (startIndex < 0) {
          startIndex = 0;
        }

        if (endIndex >= this.chartData.priceData.length) {
          endIndex = this.chartData.priceData.length - 1;
        }

        topY = -1;
        let bottomY = -1;
        left = this.chartData.priceData[startIndex].pixelStart;
        const right = this.chartData.priceData[endIndex].pixelStart;

        for (let i = startIndex; i <= endIndex; i++) {
          const dateInfo = this.chartData.priceData[i];
          const hY = panel.getYForValue(dateInfo.prices[1]);
          const lY = panel.getYForValue(dateInfo.prices[2]);

          if (topY === -1 || hY < topY) {
            topY = hY;
          }

          if (bottomY === -1 || lY > bottomY) {
            bottomY = lY;
          }
        }

        const r = new Rectangle(left, topY, right - left, bottomY - topY);
        const r2 = new Rectangle(labelX - 10, labelY - 10, labelWidth + 20, labelHeight + 20);

        if (
          r.containsPoint(labelX, labelY) ||
          r.containsPoint(labelX + labelWidth, labelY) ||
          r.containsPoint(labelX + labelWidth, labelY + labelHeight) ||
          r.containsPoint(labelX, labelY + labelHeight) ||
          r2.containsPoint(left, topY) ||
          r2.containsPoint(left + right, topY) ||
          r2.containsPoint(left + right, bottomY) ||
          r2.containsPoint(left, bottomY)
        ) {
          if (topY - (labelHeight + 2) > this.chartData.panelRectangle.y) {
            // Position above
            labelY = topY - (labelHeight + 10);
          } else {
            // Position below
            labelY = bottomY + INFO_BOX_FONT_HEIGHT + labelPadding + 10;
          }
        }

        // Bounds checks
        if (labelX + labelWidth >= this.chartData.panelRectangle.x + this.chartData.panelRectangle.width) {
          labelX = this.mouseX - (labelWidth + labelMargin);
        }

        if (labelX < this.chartData.panelRectangle.x) {
          labelX = this.chartData.panelRectangle.x;
        }

        if (labelY + labelHeight >= this.chartData.panelRectangle.y + this.chartData.panelRectangle.height) {
          labelY = this.mouseY - (labelHeight + labelMargin);
        }

        if (labelY < this.chartData.panelRectangle.y) {
          labelY = this.chartData.panelRectangle.y;
        }
      }

      this.offScreenCanvasContext.fillStyle = WHITE_COLOR;
      this.offScreenCanvasContext.strokeStyle = GRAY_COLOR;

      const LINE_BREAK_PADDING = 4;
      const beginningX = labelX - labelPadding;
      const beginningY = labelY - (INFO_BOX_FONT_HEIGHT + labelPadding);
      const infoBoxWidth = labelWidth + labelPadding * 2;
      const infoBoxHeight = INFO_BOX_FONT_HEIGHT + labelHeight + labelPadding * 2 + LINE_BREAK_PADDING;
      const cornerRounding = 3;
      this.offScreenCanvasContext.beginPath();
      this.offScreenCanvasContext.roundRect(beginningX, beginningY, infoBoxWidth, infoBoxHeight, cornerRounding);
      this.offScreenCanvasContext.fill();
      this.offScreenCanvasContext.stroke();

      for (const [i, line] of splitLines.entries()) {
        const [characterText, value] = line;

        const yLineBreakShift = i === splitLines.length - 1 ? LINE_BREAK_PADDING : 0;
        const textStartingY = labelY + (i * (INFO_BOX_FONT_HEIGHT + lineSpacing)) + yLineBreakShift;
        this.offScreenCanvasContext.fillStyle = GRAY_COLOR;
        this.offScreenCanvasContext.fillText(characterText, labelX, textStartingY);
        this.offScreenCanvasContext.fillStyle = BLACK_COLOR;
        const characterTextOffset = this.offScreenCanvasContext.measureText(characterText).width;
        this.offScreenCanvasContext.fillText(value, characterTextOffset + labelX, textStartingY);
        if (i === splitLines.length - 2) { // Draw a separation line between the last two elements
          const sectionLineY = labelY + (i * (INFO_BOX_FONT_HEIGHT + lineSpacing)) + lineSpacing / 2 + LINE_BREAK_PADDING / 2;
          this.offScreenCanvasContext.moveTo(beginningX, sectionLineY);
          this.offScreenCanvasContext.lineTo(beginningX + infoBoxWidth, sectionLineY);
          this.offScreenCanvasContext.stroke();
        }
      }
    } else if (this.mouseDownX !== null && this.mouseDownY !== null && this.direction) {
      // Num periods label
      if (this.direction === HORIZONTAL) {
          labelText = numPeriods + (numPeriods !== 1 ? ' periods' : 'period');
      } else {
        const mouseDownPanel = this.chartData.getPanelForY(this.mouseDownY);
        const startValue = mouseDownPanel.getValueForY(this.mouseDownY);
        const endValue = mouseDownPanel.getValueForY(this.mouseY);
        const diff = endValue - startValue;
        let perc;

        if (startValue !== 0) {
          perc = ((endValue - startValue) / startValue) * 100;
        } else if (startValue < endValue) {
          perc = 100;
        } else {
          perc = -100;
        }

        labelText = perc.toFixed(2) + '%  ' + formatNumber(diff, { desiredNumberOfDecimals: mouseDownPanel.getNumberOfDecimalPlaces(), shouldInsertCommas: true });
      }

      labelMargin = 10;
      labelX = this.chartData.panelRectangle.x + labelPadding + labelMargin;
      labelY = this.mouseY + LABEL_FONT_HEIGHT / 2;
      labelTextWidth = this.offScreenCanvasContext.measureText(labelText).width;
      this.offScreenCanvasContext.fillStyle = WHITE_COLOR;
      this.fillRect(this.offScreenCanvasContext, labelX - labelPadding, labelY - (LABEL_FONT_HEIGHT + labelPadding), labelTextWidth + labelPadding * 2, LABEL_FONT_HEIGHT + labelPadding * 2);

      this.offScreenCanvasContext.fillStyle = GRAY_COLOR;
      this.offScreenCanvasContext.fillText(labelText, labelX, labelY);
    }

    // Blit to screen
    this.clearCanvas(this.onScreenCanvasElement);
    this.onScreenCanvasContext.drawImage(this.offScreenCanvasElement, 0, 0);
  }
}
